/**
 * salaryUtils.js
 * Fixed-breakdown salary model (no CTC-driven tier formulas):
 *  - Gross salary components (basic/hra/lta/medical/telephone/special) are FIXED
 *    values stored per-employee in Firestore (`salaryBreakdown`). Admin enters
 *    them manually; the calculator below only SUGGESTS a starting point from a
 *    target monthly gross — it is not re-applied automatically afterwards.
 *  - Variable Pay Pool, Retention Bonus Pool, Joining Bonus (`variableComponents`)
 *    are always manually filled — no formula derives them.
 *  - Total CTC = annual gross (fixed components × 12) + variable pool +
 *    retention pool + joining bonus.
 */

/** Strip formatting from money strings like "₹8,00,000/-" → 800000 */
export const parseCTCNumber = (str = '') => {
  const cleaned = String(str).replace(/[₹,\s\-/]/g, '').replace(/[^0-9].*/, '');
  const n = parseFloat(cleaned);
  return isNaN(n) ? 0 : n;
};

/** Round to 2 decimal places to preserve sub-rupee precision */
export const r2 = (n) => Math.round(n * 100) / 100;

/** Format number in Indian numbering system with up to 2 decimal places */
export const fmtINR = (n) => { const v = r2(n); return v.toLocaleString('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 0 }); };

/**
 * Gross salary calculator — suggests Basic/HRA/LTA/Medical/Telephone/Special
 * for a target monthly gross. Purely a prefill helper; admin can edit every
 * field before saving it as the employee's fixed breakdown.
 */
export const computeGrossBreakdown = (monthlyGross) => {
  const g = r2(Number(monthlyGross) || 0);
  if (!g || g <= 0) return null;
  const basic     = r2(g * 0.50);
  const hra       = r2(basic * 0.40);
  const lta       = g >= 100000 ? 3000 : g >= 50000 ? 1500 : g >= 25000 ? 1000 : 500;
  const medical   = g >= 100000 ? 2000 : g >= 50000 ? 1250 : g >= 25000 ? 1000 : 600;
  const telephone = g >= 100000 ? 2500 : g >= 50000 ? 1250 : g >= 25000 ? 1000 : 600;
  const special   = r2(g - basic - hra - lta - medical - telephone);
  return { basic, hra, lta, medical, telephone, special };
};

/** Sum a fixed breakdown's components into monthly/annual gross */
export const sumGrossBreakdown = (b) => {
  const monthlyGross = r2(
    (Number(b?.basic) || 0) + (Number(b?.hra) || 0) + (Number(b?.lta) || 0) +
    (Number(b?.medical) || 0) + (Number(b?.telephone) || 0) + (Number(b?.special) || 0)
  );
  return { monthlyGross, annualGross: r2(monthlyGross * 12) };
};

/** Split a retention pool into 5 equal yearly tranches (rounding remainder in last) */
export const splitRetentionTranches = (retentionPool) => {
  const pool = Number(retentionPool) || 0;
  const t = r2(pool / 5);
  const t5 = r2(pool - t * 4);
  return { t1: t, t2: t, t3: t, t4: t, t5 };
};

/**
 * Scale an EXISTING fixed breakdown to a new monthly gross, preserving each
 * component's ratio (Basic/HRA/LTA/Medical/Telephone stay proportional;
 * Special Allowance absorbs the rounding remainder). Used on appraisals so
 * the fixed breakdown structure is maintained instead of being re-typed.
 */
export const scaleGrossBreakdown = (oldBreakdown, newMonthlyGross) => {
  const { monthlyGross: oldGross } = sumGrossBreakdown(oldBreakdown);
  const target = r2(Number(newMonthlyGross) || 0);
  if (!oldGross || !target || target <= 0) return null;
  const factor    = target / oldGross;
  const basic     = r2((Number(oldBreakdown?.basic) || 0) * factor);
  const hra       = r2((Number(oldBreakdown?.hra) || 0) * factor);
  const lta       = r2((Number(oldBreakdown?.lta) || 0) * factor);
  const medical   = r2((Number(oldBreakdown?.medical) || 0) * factor);
  const telephone = r2((Number(oldBreakdown?.telephone) || 0) * factor);
  const special   = r2(target - basic - hra - lta - medical - telephone);
  return { basic, hra, lta, medical, telephone, special };
};

/** Total CTC = annual gross (fixed) + variable pool + retention pool + joining bonus */
export const computeTotalCTC = (breakdown, variableComponents = {}) => {
  const { annualGross } = sumGrossBreakdown(breakdown);
  const variablePool  = Number(variableComponents?.variablePayPool) || 0;
  const retentionPool = Number(variableComponents?.retentionPool) || 0;
  const joiningBonus  = Number(variableComponents?.joiningBonus) || 0;
  return r2(annualGross + variablePool + retentionPool + joiningBonus);
};
